import { useState } from 'react';
import { api, ApiError, historyApi, type HistoryMeeting, type HistoryMeetingRun, type HistoryMeetingsView } from '../../api/client';
import { useTenant } from '../../components/Shell';
import { Button, Card, Drawer, Empty, LoadError, Loading, Notice, PageHeader, SelectField } from '../../components/ui';
import { clockTime, fullWhen, plural, sizeOf } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { timesIn } from '../../lib/time';
import { useData } from '../../lib/useData';
import { duration } from './Calls';
import { ArchiveNotices, HistoryTabs, kept, lengthBetween, ListFoot, SearchBox, useArchiveList, useHistoryFilters, useMember, WhenFields } from './HistoryArchive';

/**
 * History's Meetings tab (Storage A3, 1.13.0, Teampace 3086; help: call-history): every archived run of
 * a meeting or webinar, searched over every date the archive holds; one opens with who came (and who
 * the lobby turned away), what happened, its recording and transcript, and two downloads: the
 * attendance report (CSV) and the chat and events (text). Everyone sees the meetings they hosted or
 * were in; admins every one. Each watch and download is logged.
 */

const PAGE_SIZE = 50;

const KINDS = [
  { value: '', label: 'Meetings and webinars' },
  { value: 'meeting', label: 'Meetings' },
  { value: 'webinar', label: 'Webinars' }
];

const OUTCOME: Record<string, string> = { attended: 'Came', turned_away: 'Not let in', waited: 'Left the lobby', invited: 'Didn’t come' };

/** What happened, in words (the chat's lines as said). */
export function meetingEventLine(e: { kind: string; who: string; text: string | null }): string {
  switch (e.kind) {
    case 'chat':
      return `${e.who}: ${e.text ?? ''}`;
    case 'question':
      return `${e.who} asked: ${e.text ?? ''}`;
    case 'answer':
      return `${e.who} answered: ${e.text ?? ''}`;
    case 'poll':
      return `Poll by ${e.who}: ${e.text ?? ''}`;
    case 'joined':
      return `${e.who} joined`;
    case 'left':
      return `${e.who} left`;
    case 'lobby_asked':
      return `${e.who} asked to come in`;
    case 'let_in':
      return `${e.who} was let in`;
    case 'turned_away':
      return `${e.who} wasn’t let in`;
    case 'recording_started':
      return `${e.who || 'Someone'} started recording`;
    case 'recording_stopped':
      return 'Recording stopped';
    default:
      return `${e.who} ${e.kind.replace(/_/g, ' ')}`;
  }
}

export function MeetingHistory() {
  const { tenant } = useTenant();
  const f = useHistoryFilters();
  const person = (f.params.get('person') ?? '').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
  const kind = (['meeting', 'webinar'] as const).find((k) => k === f.params.get('kind'));
  const recorded = f.params.get('recorded') === 'yes';
  const member = useMember(tenant.domain);
  const peopleList = useData(() => (member ? api.messagePeople(tenant.domain).catch(() => ({ people: [] })) : Promise.resolve({ people: [] })), [tenant.domain, member], { live: false });
  const search = { from: f.from, to: f.to, ...(f.q ? { q: f.q } : {}), ...(person ? { person } : {}), ...(kind ? { kind } : {}), ...(recorded ? { recorded: 'yes' as const } : {}) };
  const list = useArchiveList<HistoryMeetingsView, HistoryMeeting>((cursor) => historyApi.meetings(tenant.domain, { ...search, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }), (v) => v.meetings, `${tenant.domain}${JSON.stringify(search)}`);
  const [open, setOpen] = useState<HistoryMeeting | null>(null);
  const filtered = Boolean(f.q || person || kind || recorded || f.when !== '30');
  return (
    <>
      <PageHeader
        eyebrow={`${list.items.length ? `${plural(list.items.length, 'meeting')}${list.last?.next ? '+' : ''} · ` : ''}${timesIn()}`}
        title="Meeting history"
        actions={<SearchBox f={f} label="Search meetings" placeholder="Search a title or a name" />}
      />
      <div className="content">
        <HistoryTabs />
        <div className="call-filters" role="group" aria-label="Filter meetings">
          <WhenFields f={f} />
          <SelectField label="Kind" value={kind ?? ''} onChange={(e) => f.set({ kind: e.target.value })} options={KINDS} />
          <SelectField label="With" value={person} onChange={(e) => f.set({ person: e.target.value })} options={[{ value: '', label: 'Anyone' }, ...(peopleList.data?.people ?? []).map((p) => ({ value: p.id, label: p.name }))]} />
          <label className="check-row history-check">
            <input type="checkbox" checked={recorded} onChange={(e) => f.set({ recorded: e.target.checked ? 'yes' : undefined })} />
            <span>Recorded only</span>
          </label>
          {filtered && (
            <div className="call-filters-clear">
              <Button variant="ghost" onClick={f.clear}>
                Clear filters
              </Button>
            </div>
          )}
        </div>
        {list.last?.scope === 'own' && <Notice>Showing the meetings you hosted or were in.</Notice>}
        <ArchiveNotices state={list.last?.archive} keptSince={list.last?.keptSince} from={f.from} done={!list.loading && !list.last?.next} what="meetings" />
        <Card>
          {list.error && list.items.length === 0 ? (
            <LoadError error={list.error} retry={list.retry} />
          ) : list.loading && list.items.length === 0 ? (
            <Loading label="Loading meetings" />
          ) : list.items.length === 0 ? (
            <Empty title={filtered ? 'No meetings match' : 'No meetings in the archive yet'}>{filtered ? 'Try other dates, or fewer filters.' : 'A meeting is archived within a few minutes of ending.'}</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table calls">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Meeting</th>
                    <th scope="col">Host</th>
                    <th scope="col">Who came</th>
                    <th scope="col">Length</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map((x) => (
                    <tr key={`${x.from_}-${x.id}`}>
                      <td>
                        <span className="call-when">
                          {fullWhen(x.startedAt)}
                          {kept(x.from_)}
                        </span>
                      </td>
                      <td>
                        <button type="button" className="link-btn" onClick={() => setOpen(x)} aria-label={`Open ${x.title || 'the meeting'} at ${fullWhen(x.startedAt)}`}>
                          {x.title || 'Meeting'}
                        </button>
                        <div className="muted small">
                          {x.kind === 'webinar' ? 'Webinar' : 'Meeting'}
                          {x.recorded ? ' · recorded' : ''}
                        </div>
                      </td>
                      <td>{x.host.name || <span className="muted">Unknown</span>}</td>
                      <td>
                        {plural(x.people, 'person', 'people')}
                        {x.guests ? `, ${plural(x.guests, 'guest')}` : ''}
                        {x.names.length > 0 && <div className="muted small history-snippet">{x.names.join(', ')}</div>}
                      </td>
                      <td className="mono">{duration(lengthBetween(x.startedAt, x.endedAt))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ListFoot show={list.items.length > 0 && Boolean(list.last?.next)} loading={list.loading} onMore={() => void list.more()} />
        </Card>
      </div>
      <Drawer open={open !== null} onClose={() => setOpen(null)} title={open ? open.title || 'Meeting' : 'Meeting'} subtitle={open ? fullWhen(open.startedAt) : ''}>
        {open && <MeetingRun domain={tenant.domain} m={open} member={member} />}
      </Drawer>
    </>
  );
}

/** One run in full: its recording, its people, what happened, and the two downloads. */
function MeetingRun({ domain, m, member }: { domain: string; m: HistoryMeeting; member: boolean }) {
  const data = useData(() => historyApi.meeting(domain, m.id, m.startedAt), [domain, m.id, m.startedAt], { live: false });
  if (data.error) return <Notice tone="warn">{data.error instanceof ApiError ? data.error.message : 'The archive couldn’t send this just now.'}</Notice>;
  if (!data.data) return <Loading label="Finding it in the archive" />;
  const r: HistoryMeetingRun = data.data;
  const video = r.files.find((x) => x.type.startsWith('video/'));
  return (
    <div className="stack">
      <dl className="dl">
        <dt>Host</dt>
        <dd>{r.meeting.host.name || 'Unknown'}</dd>
        <dt>Length</dt>
        <dd>{duration(lengthBetween(m.startedAt, m.endedAt))}</dd>
        <dt>Who came</dt>
        <dd>
          {plural(r.people.filter((p) => p.joinedAt).length, 'person', 'people')}
          {r.people.some((p) => p.outcome === 'turned_away') ? `; ${plural(r.people.filter((p) => p.outcome === 'turned_away').length, 'guest')} not let in` : ''}
        </dd>
      </dl>
      <div className="row-actions">
        <a className="btn secondary small" href={historyApi.meetingAttendanceUrl(domain, m.id, m.startedAt)} download>
          <Icon name="download" size={14} /> Attendance report
        </a>
        {member && r.member && (
          <a className="btn ghost small" href={historyApi.meetingChatUrl(domain, m.id, m.startedAt)} download>
            <Icon name="download" size={14} /> Chat and events
          </a>
        )}
      </div>
      {r.files.length > 0 && (
        <>
          <h3 className="section-title">Recording</h3>
          {video && <video className="history-video" controls preload="none" src={historyApi.meetingFileUrl(domain, m.id, video.n, m.startedAt)} aria-label="Meeting recording" />}
          {r.files.map((x) => (
            <a key={x.n} className="chip-link" href={historyApi.meetingFileUrl(domain, m.id, x.n, m.startedAt, true)} download>
              <Icon name="download" size={14} /> Download {x.type.startsWith('video/') ? 'the video' : 'the sound only'}
              {x.bytes ? ` (${sizeOf(x.bytes)})` : ''}
            </a>
          ))}
        </>
      )}
      {r.transcript && (
        <>
          <h3 className="section-title">Transcript</h3>
          <p className="history-transcript">{r.transcript}</p>
        </>
      )}
      <h3 className="section-title">Who came</h3>
      {r.people.length === 0 ? (
        <p className="muted small">Nobody is listed for this meeting.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Joined</th>
                <th scope="col">Left</th>
                <th scope="col">Time in</th>
              </tr>
            </thead>
            <tbody>
              {r.people.map((p, i) => (
                <tr key={`${p.name}-${i}`}>
                  <td>
                    {p.name || 'Someone'}
                    <div className="muted small">
                      {p.kind === 'guest' ? 'Guest' : p.email || 'In Stitch'}
                      {p.role !== 'attendee' ? ` · ${p.role}` : ''}
                      {p.outcome !== 'attended' ? ` · ${OUTCOME[p.outcome] ?? p.outcome}` : ''}
                      {p.answeredByName && p.kind === 'guest' ? ` (${p.outcome === 'turned_away' ? 'by' : 'let in by'} ${p.answeredByName})` : ''}
                    </div>
                  </td>
                  <td>{p.joinedAt ? clockTime(p.joinedAt) : '–'}</td>
                  <td>{p.leftAt ? clockTime(p.leftAt) : '–'}</td>
                  <td className="mono">{p.seconds ? duration(p.seconds) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3 className="section-title">What happened</h3>
      {r.events.length === 0 ? (
        <p className="muted small">Nothing is listed for this meeting.</p>
      ) : (
        <ul className="history-events">
          {r.events.map((e, i) => (
            <li key={`${e.at}-${i}`}>
              <span className="mono muted">{clockTime(e.at)}</span> {meetingEventLine(e)}
            </li>
          ))}
        </ul>
      )}
      {!r.member && <p className="muted small">A meeting’s recording, transcript and chat are for the company’s own people.</p>}
      <p className="muted small">Watched and downloaded through Stitch, from its archive. Each one is logged.</p>
    </div>
  );
}
